import { ArrowRight, Loader2, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { IssueStatusChip } from "@/components/issue-status-chip";
import { StatusBadge } from "@/components/status-badge";
import { RetiredChip } from "@/components/tool-name";
import { Card, CardContent } from "@/components/ui/card";
import type { ConnectorOverview, McpIssue, McpIssueAttention } from "@/lib/api";
import { connectorStatus, odooStatus, type StatusLine } from "@/lib/connector-status";
import { formatDateTime } from "@/lib/format";
import {
  FAILURES_PATH,
  issueKindLabel,
  issuePath,
  issueToolLabel,
  needsAnalysis,
} from "@/lib/issues";
import { type Period, PERIODS } from "@/lib/period";
import { factLine } from "@/lib/table-facts";
import { isRetiredTool, useToolTitles } from "@/lib/tool-titles";
import { cn } from "@/lib/utils";

/** How many issues the home page lists before it points to Fouten for the rest. */
const SHOWN_ISSUES = 5;

/**
 * The longest period of Statistieken, for the issues that did not fail this
 * week, and the most the Fouten list shows: the counts behind its links.
 */
export const QUARTER: Period = 90;

/**
 * Fouten filtered on the issues Velta is on, over the last quarter: an
 * acknowledged issue stays so while it fails again, so it may not have
 * failed in the last week.
 */
const ACKNOWLEDGED_ISSUES_PATH = `${FAILURES_PATH}?days=${QUARTER}&status=acknowledged`;

/** Fouten filtered on the issues still to analyse, over the last quarter, for the ones the list leaves out. */
const OPEN_ISSUES_PATH = `${FAILURES_PATH}?days=${QUARTER}&status=new&status=regressed&status=open`;

interface ConnectorCardProps {
  /** The overview; undefined while it loads or when it failed. */
  overview: ConnectorOverview | undefined;
  isLoading: boolean;
  onRetry: () => void;
  /**
   * The issues still to analyse and how many Velta is on, for an admin;
   * undefined for everyone else, while they load and after they failed.
   */
  attention?: McpIssueAttention;
  /**
   * The issues with a failure in the last `QUARTER` days, the most the Fouten
   * list can show: the links to it count only these, so a link never opens a
   * list without the issues it counts. Undefined while they load and after
   * they failed: the links then wait.
   */
  quarterIssues?: readonly McpIssue[];
  className?: string;
}

/**
 * The connector at a glance: whether it is on (and how people sign in) and
 * whether the Odoo tools are on, with the way to the Tools page. Under it,
 * for an admin, the failures still to analyse, each with the way to its
 * issue, and how many Velta is on. Nothing at all when there is none.
 */
export function ConnectorCard({
  overview,
  isLoading,
  onRetry,
  attention,
  quarterIssues,
  className,
}: ConnectorCardProps) {
  const { t } = useTranslation();
  return (
    <Card className={cn("flex flex-col", className)}>
      <CardContent className="flex flex-1 flex-col gap-5 pt-6 max-sm:px-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-heading text-primary text-lg font-bold">{t("nav.connector")}</h2>
          <Link
            to="/tools"
            className="text-accent tap-target inline-flex items-center gap-1 text-sm font-medium hover:underline pointer-coarse:relative"
          >
            {t("nav.tools")}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center py-6">
            <Loader2 className="text-muted h-6 w-6 animate-spin" aria-label={t("common.loading")} />
          </div>
        ) : overview ? (
          <dl className="space-y-4">
            <StatusRow term={t("home.status")} line={connectorStatus(overview.mode)} />
            <StatusRow term={t("home.odooTools")} line={odooStatus(overview)} />
          </dl>
        ) : (
          <p className="text-muted py-6 text-center text-sm">
            {t("home.statusLoadError")}{" "}
            <button type="button" onClick={onRetry} className="underline">
              {t("common.tryAgain")}
            </button>
          </p>
        )}
        {attention && attention.issues.length > 0 && (
          <Issues attention={attention} quarterIssues={quarterIssues} />
        )}
      </CardContent>
    </Card>
  );
}

interface IssuesProps {
  attention: McpIssueAttention;
  quarterIssues: readonly McpIssue[] | undefined;
}

/**
 * The failures still to analyse, newest first: each issue with an alert, its
 * kind and tool ("Niet meer beschikbaar" for a tool no longer served), how
 * often it failed in the last 7 days, "Teruggekomen" when it failed again
 * after a fix, and the way to its page. Under the list, the way to the others
 * still to analyse and to the issues Velta is on, on Fouten over the last
 * quarter, each counting the issues that list shows.
 */
function Issues({ attention, quarterIssues = [] }: IssuesProps) {
  const { t } = useTranslation();
  const titles = useToolTitles();
  const shown = attention.issues.slice(0, SHOWN_ISSUES);
  const listed = new Set(shown.map((issue) => issue.key));
  const more = quarterIssues.filter(
    (issue) => needsAnalysis(issue.status) && !listed.has(issue.key),
  ).length;
  const acknowledged = quarterIssues.filter((issue) => issue.status === "acknowledged").length;
  return (
    <section aria-labelledby="home-issues" className="border-border border-t pt-4">
      <h3 id="home-issues" className="text-foreground text-sm font-semibold">
        {t("home.issues.title")}
      </h3>
      <ul className="mt-2 space-y-2.5">
        {shown.map((issue) => (
          <li key={issue.key} data-issue={issue.key} className="flex items-start gap-2 text-sm">
            <TriangleAlert
              aria-hidden="true"
              className="text-destructive mt-0.5 h-4 w-4 shrink-0"
              strokeWidth={2}
            />
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <Link
                  // An issue that last failed before this week opens on the last quarter.
                  to={issuePath(issue.key, issue.count > 0 ? PERIODS[0] : QUARTER)}
                  className="text-foreground font-medium hover:underline"
                >
                  {issueKindLabel(issue.error_type)}
                </Link>
                {issue.status === "regressed" && <IssueStatusChip status={issue.status} />}
              </p>
              <p className="text-muted flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                <span>
                  {factLine([
                    issueToolLabel(issue.tool, titles),
                    issue.count > 0
                      ? t("home.issues.failedLastWeek", { count: issue.count })
                      : t("home.issues.lastFailed", { time: formatDateTime(issue.last_seen) }),
                  ])}
                </span>
                {issue.tool !== null && isRetiredTool(titles, issue.tool) && <RetiredChip />}
              </p>
            </div>
          </li>
        ))}
      </ul>
      {(more > 0 || acknowledged > 0) && (
        <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {more > 0 && (
            <Link
              to={OPEN_ISSUES_PATH}
              className="text-accent tap-target font-medium hover:underline pointer-coarse:relative"
            >
              {t("home.issues.more", { count: more })}
            </Link>
          )}
          {acknowledged > 0 && (
            <Link
              to={ACKNOWLEDGED_ISSUES_PATH}
              className="text-accent tap-target font-medium hover:underline pointer-coarse:relative"
            >
              {t("home.issues.acknowledged", { count: acknowledged })}
            </Link>
          )}
        </p>
      )}
    </section>
  );
}

function StatusRow({ term, line }: { term: string; line: StatusLine }) {
  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-0.5">
      <dt className="text-foreground text-sm font-semibold">{term}</dt>
      <dd>
        <StatusBadge on={line.on} label={line.label} />
      </dd>
      <dd className="text-muted col-span-2 text-sm">{line.detail}</dd>
    </div>
  );
}
